import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Bot, Plus, Send, Search, Star, Archive, Loader2, AlertCircle,
    MessageSquare, ChevronLeft, ChevronRight, Download, Save, X,
    Maximize2, Minimize2, FileText, Check, RefreshCw, GripVertical,
    Paperclip, Upload, File, Image, FolderOpen, Trash2, ExternalLink,
    Mail, User, CheckCircle, ArrowRight, HelpCircle, Trophy, Clock,
    Award, BookOpen, Lightbulb, Target, AlertTriangle, Sparkles,
    Expand, PanelLeftClose, PanelLeft, Edit3, Copy, Eye, Pencil,
    Type, Hash, AlignLeft
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { AIAnswerWithCitations, SourcesList } from '../components/AI/CitationBadge';
import UnifiedFilePickerModal from '../components/Common/UnifiedFilePickerModal';


// ============================================
// Employee AI Assistant Page
// ============================================
// Features:
// - Full-screen layout: 20% sidebar, 80% chat/editor
// - Split-screen: 40% chat, 60% editor when document active
// - Resizable split (25%-75% range)
// - Document editor with Download, Save, Close
// - Format selection modal
// - Unsaved changes warning
// - Live update indicator
// - File upload with drag-drop and validation 
// - File processing progress UI
// - Source citations in AI responses
// ============================================

// File validation constants
const ALLOWED_EXTENSIONS = ['pdf', 'docx', 'doc', 'xlsx', 'xls', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
const BLOCKED_EXTENSIONS = ['exe', 'js', 'py', 'zip', 'rar', 'bat', 'sh', 'cmd', 'ps1'];
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

// File validation helper
const validateFile = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();

    if (BLOCKED_EXTENSIONS.includes(ext)) {
        return { valid: false, error: `File type .${ext} is not allowed for security reasons` };
    }

    if (!ALLOWED_EXTENSIONS.includes(ext)) {
        return { valid: false, error: `File type .${ext} is not supported. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}` };
    }

    if (file.size > MAX_FILE_SIZE) {
        return { valid: false, error: `File size exceeds 25 MB limit (${(file.size / 1024 / 1024).toFixed(1)} MB)` };
    }

    return { valid: true };
};

// File icon helper
const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return '🖼️';
    if (['pdf'].includes(ext)) return '📑';
    if (['docx', 'doc'].includes(ext)) return '📄';
    if (['xlsx', 'xls'].includes(ext)) return '📊';
    return '📎';
};

// Format Selection Modal - Light Theme
const FormatModal = ({ isOpen, onClose, onSelect }) => {
    if (!isOpen) return null;

    const formats = [
        { id: 'docx', label: 'Word Document', ext: '.docx', icon: FileText, color: 'text-blue-500' },
        { id: 'pdf', label: 'PDF Document', ext: '.pdf', icon: FileText, color: 'text-red-500' },
        { id: 'txt', label: 'Plain Text', ext: '.txt', icon: FileText, color: 'text-gray-500' },
        { id: 'xlsx', label: 'Excel Spreadsheet', ext: '.xlsx', icon: FileText, color: 'text-green-500' },
    ];

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-100 transform transition-all animate-in fade-in zoom-in-95 duration-200"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                            <Download className="text-primary" size={20} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-800">Download Format</h3>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                        <X size={18} />
                    </button>
                </div>
                <div className="space-y-2">
                    {formats.map(format => (
                        <button
                            key={format.id}
                            onClick={() => onSelect(format.id)}
                            className="w-full flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 border border-transparent hover:border-primary/30 transition-all duration-200 text-left group"
                        >
                            <div className={`w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform ${format.color}`}>
                                <format.icon size={20} />
                            </div>
                            <div className="flex-1">
                                <span className="text-gray-800 font-medium block">{format.label}</span>
                                <span className="text-xs text-gray-500">{format.ext}</span>
                            </div>
                            <ArrowRight size={16} className="text-gray-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Save As Modal - Name the file before saving
const SaveAsModal = ({ isOpen, onClose, onSave, defaultTitle, isSaving }) => {
    const [fileName, setFileName] = useState(defaultTitle || '');
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setFileName(defaultTitle || 'AI Generated Document');
            setTimeout(() => inputRef.current?.select(), 100);
        }
    }, [isOpen, defaultTitle]);

    if (!isOpen) return null;

    const handleSave = () => {
        if (!fileName.trim()) {
            toast.error('Please enter a file name');
            return;
        }
        onSave(fileName.trim());
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-100"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Save className="text-primary" size={20} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-800">Save Document</h3>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                        <X size={18} />
                    </button>
                </div>

                <div className="mb-5">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">File Name</label>
                    <input
                        ref={inputRef}
                        type="text"
                        value={fileName}
                        onChange={(e) => setFileName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                        placeholder="Enter a name for your document..."
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all placeholder:text-gray-400"
                        autoFocus
                    />
                    <p className="text-xs text-gray-400 mt-1.5">This will be saved as a text file in My Files</p>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-700 font-medium transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!fileName.trim() || isSaving}
                        className="flex-1 py-3 bg-primary hover:bg-primary-hover rounded-xl text-white font-medium hover:shadow-lg hover:shadow-primary/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {isSaving ? (
                            <><Loader2 className="animate-spin" size={18} /> Saving...</>
                        ) : (
                            <><Save size={18} /> Save</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Save Confirmation Modal - Light Theme
const SaveConfirmModal = ({ isOpen, onClose, onViewFiles, onDownloadAlso }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-100"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex flex-col items-center text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
                        <CheckCircle className="text-success" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Document Saved!</h3>
                    <p className="text-gray-500 text-sm">Your document has been saved to My Files.</p>
                </div>
                <div className="flex flex-col gap-3">
                    <button
                        onClick={onViewFiles}
                        className="w-full py-3 bg-primary hover:bg-primary-hover rounded-xl text-white font-medium hover:shadow-lg hover:shadow-primary/30 transition-all flex items-center justify-center gap-2"
                    >
                        <FolderOpen size={18} />
                        View in My Files
                    </button>
                    <button
                        onClick={onDownloadAlso}
                        className="w-full py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 font-medium transition-colors flex items-center justify-center gap-2 border border-gray-200"
                    >
                        <Download size={18} />
                        Download Also
                    </button>
                    <button
                        onClick={onClose}
                        className="w-full py-2.5 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// Unsaved Changes Warning Modal - Light Theme
const UnsavedWarningModal = ({ isOpen, onClose, onDiscard, onSave, onDownload }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl border border-warning/30"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="text-warning" size={24} />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-800">Unsaved Changes</h3>
                        <p className="text-sm text-gray-500">Your work will be lost if you don't save</p>
                    </div>
                </div>
                <div className="flex flex-col gap-3">
                    <button
                        onClick={onSave}
                        className="w-full py-3 bg-primary hover:bg-primary-hover rounded-xl text-white font-medium hover:shadow-lg hover:shadow-primary/30 transition-all flex items-center justify-center gap-2"
                    >
                        <Save size={18} />
                        Save to My Files
                    </button>
                    <button
                        onClick={onDownload}
                        className="w-full py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 font-medium transition-colors flex items-center justify-center gap-2 border border-gray-200"
                    >
                        <Download size={18} />
                        Download
                    </button>
                    <button
                        onClick={onDiscard}
                        className="w-full py-2.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                        <Trash2 size={16} />
                        Discard Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

// Admin Message Modal - Phase 6
const AdminMessageModal = ({ isOpen, onClose, onSent, context, navigate }) => {
    const [admins, setAdmins] = useState([]);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [sentSuccess, setSentSuccess] = useState(false);

    // Pre-fill message based on context
    useEffect(() => {
        if (isOpen && context) {
            const prefill = `Hi, I was talking to the AI Assistant and need some help with:\n\n"${context}"\n\nCould you please assist me?`;
            setMessage(prefill);
        }
    }, [isOpen, context]);

    // Fetch admins when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchAdmins();
        } else {
            // Reset state when closed
            setSelectedAdmin(null);
            setMessage('');
            setSentSuccess(false);
        }
    }, [isOpen]);

    const fetchAdmins = async () => {
        try {
            setIsLoading(true);
            // Fetch actual admins from the company
            const response = await api.get('/messages/employee/admins');
            if (response.data.admins) {
                setAdmins(response.data.admins.map(admin => ({
                    id: admin.id,
                    name: `${admin.first_name} ${admin.last_name}`,
                    role: admin.position || 'Administrator',
                    avatar: admin.avatar,
                })));
            } else {
                // Fallback mock data if API not available
                setAdmins([
                    { id: 1, name: 'HR Admin', role: 'HR Manager', avatar: null },
                ]);
            }
        } catch (error) {
            console.error('Error fetching admins:', error);
            // Fallback to mock if API fails
            setAdmins([
                { id: 1, name: 'HR Admin', role: 'HR Manager', avatar: null },
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSend = async () => {
        if (!selectedAdmin || !message.trim()) {
            toast.error('Please select an admin and enter a message');
            return;
        }

        try {
            setIsSending(true);
            let conversationId = null;

            // Create or get conversation with admin and send message
            try {
                // First, try to find existing conversation or create new one
                const convResponse = await api.post('/messages/employee/create-conversation', {
                    adminId: selectedAdmin.id,
                    subject: 'AI Assistant Question',
                });

                conversationId = convResponse.data.conversation?.id || convResponse.data.conversationId;

                if (conversationId) {
                    // Send the message to that conversation
                    await api.post(`/messages/conversations/${conversationId}/messages`, {
                        content: message.trim(),
                    });
                }
            } catch (apiError) {
                console.log('Messages API not available, simulating send:', apiError);
                // Simulate send if API not implemented yet
                await new Promise(r => setTimeout(r, 500));
            }

            setSentSuccess(true);
            toast.success('Message sent successfully!');

            if (onSent) {
                onSent({ adminId: selectedAdmin.id, message, conversationId });
            }

            // Close modal and navigate to messages after short delay
            setTimeout(() => {
                onClose();
                if (navigate && conversationId) {
                    navigate('/employee/messages', { state: { selectedConversationId: conversationId } });
                } else if (navigate) {
                    navigate('/employee/messages');
                }
            }, 1500);
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
        } finally {
            setIsSending(false);
        }
    };

    if (!isOpen) return null;

    if (sentSuccess) {
        return (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
                <div
                    className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl border border-gray-100 text-center"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="text-success" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Message Sent!</h3>
                    <p className="text-gray-500 mb-6">
                        Your message has been sent to {selectedAdmin?.name}. They'll get back to you soon.
                    </p>
                    <button
                        onClick={onClose}
                        className="w-full py-3 bg-success hover:bg-success-light rounded-xl text-white font-medium transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
            <div
                className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 shadow-2xl border border-gray-100 max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center">
                            <Mail className="text-primary" size={20} />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-800">Message Admin</h3>
                            <p className="text-xs text-gray-500">Get help from your team</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600">
                        <X size={20} />
                    </button>
                </div>

                {/* Admin Selection */}
                <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Select Admin</label>
                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="animate-spin text-primary" size={24} />
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {admins.map(admin => (
                                <button
                                    key={admin.id}
                                    onClick={() => setSelectedAdmin(admin)}
                                    className={`
                                                w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all border
                                                ${selectedAdmin?.id === admin.id
                                            ? 'bg-orange-50 border-primary shadow-sm'
                                            : 'bg-white border-gray-200 hover:border-primary/30 hover:shadow-sm'}
                                            `}
                                >
                                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                                        <User size={18} className="text-gray-500" />
                                    </div>
                                    <div className="flex-1">
                                        <p className={`font-medium ${selectedAdmin?.id === admin.id ? 'text-primary' : 'text-gray-800'}`}>{admin.name}</p>
                                        <p className="text-xs text-gray-500">{admin.role}</p>
                                    </div>
                                    {selectedAdmin?.id === admin.id && (
                                        <Check className="text-primary" size={20} />
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Message Input */}
                <div className="mb-6">
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Your Message</label>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Describe what you need help with..."
                        rows={5}
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-800 text-sm resize-none focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all placeholder:text-gray-400"
                    />

                </div>

                {/* Actions */}
                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-700 font-medium transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={!selectedAdmin || !message.trim() || isSending}
                        className="flex-1 py-3 bg-primary hover:bg-primary-hover rounded-xl text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
                    >
                        {isSending ? (
                            <Loader2 className="animate-spin" size={18} />
                        ) : (
                            <>
                                <Send size={18} />
                                Send Message
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// Smart Handoff Card Component
// ============================================
const SmartHandoffCard = ({ handoff, onSend, onDismiss, conversationId }) => {
    const [message, setMessage] = useState(handoff.primary?.prefillMessage || '');
    const [isSending, setIsSending] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const [activeRecipient, setActiveRecipient] = useState(handoff.primary);

    const handleSend = async () => {
        if (!message.trim() || !activeRecipient?.personId) return;
        setIsSending(true);
        try {
            const response = await api.post('/employee/ai/handoff', {
                recipientId: activeRecipient.personId,
                message: message.trim(),
                handoffMetadata: {
                    topic: activeRecipient.topic || 'general',
                    reason: activeRecipient.reason || '',
                    personRole: activeRecipient.personRole || '',
                    aiConversationId: conversationId,
                    suggestedBy: 'ai',
                },
            });
            setIsSent(true);
            toast.success(`Message sent to ${activeRecipient.personName}!`);
            if (onSend) onSend(response.data);
        } catch (error) {
            console.error('Handoff send error:', error);
            toast.error('Failed to send message. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    const handleDismiss = async () => {
        try {
            await api.post('/employee/ai/handoff/dismiss', {
                suggestedPersonId: activeRecipient?.personId,
                topic: activeRecipient?.topic,
                aiConversationId: conversationId,
            });
        } catch (e) {
            // Silent fail for analytics
        }
        if (onDismiss) onDismiss();
    };

    const switchToAlternative = () => {
        if (handoff.alternative) {
            setActiveRecipient(handoff.alternative);
            setMessage(handoff.alternative.prefillMessage || handoff.primary?.prefillMessage || '');
        }
    };

    if (isSent) {
        return (
            <div className="mt-3 bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle size={16} />
                    <span className="text-sm font-medium">Message sent to {activeRecipient?.personName}</span>
                </div>
                <p className="text-xs text-green-600 mt-1">They'll get back to you soon.</p>
            </div>
        );
    }

    if (!activeRecipient) return null;

    return (
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                        <ArrowRight size={12} className="text-blue-600" />
                    </div>
                    <span className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Suggested Contact</span>
                </div>
                <button
                    onClick={handleDismiss}
                    className="text-blue-400 hover:text-blue-600 p-1 rounded transition-colors"
                    title="Dismiss"
                >
                    <X size={14} />
                </button>
            </div>

            {/* Person Info */}
            <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    {activeRecipient.personAvatar ? (
                        <img src={activeRecipient.personAvatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                        <User size={18} className="text-blue-600" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm">{activeRecipient.personName}</p>
                    <p className="text-xs text-gray-500">{activeRecipient.personRole}</p>
                </div>
            </div>

            {/* Reason */}
            {activeRecipient.reason && (
                <p className="text-xs text-gray-600 mb-3 leading-relaxed">{activeRecipient.reason}</p>
            )}

            {/* Editable Message */}
            <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full p-3 bg-white border border-blue-100 rounded-lg text-sm text-gray-800 resize-none focus:outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200 transition-all"
                rows={3}
                placeholder="Edit your message before sending..."
            />

            {/* Actions */}
            <div className="flex items-center gap-2 mt-3">
                <button
                    onClick={handleSend}
                    disabled={isSending || !message.trim()}
                    className="flex-1 py-2.5 bg-primary hover:bg-primary-hover text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
                >
                    {isSending ? (
                        <Loader2 className="animate-spin" size={14} />
                    ) : (
                        <Mail size={14} />
                    )}
                    {isSending ? 'Sending...' : `Message ${activeRecipient.personName.split(' ')[0]}`}
                </button>
            </div>

            {/* Alternative person */}
            {handoff.alternative && activeRecipient.personId !== handoff.alternative.personId && (
                <button
                    onClick={switchToAlternative}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                >
                    Or contact {handoff.alternative.personName} ({handoff.alternative.personRole}) instead
                </button>
            )}
        </div>
    );
};

// ============================================
// Phase 7: Proactive Help & Education Components
// ============================================

// Platform Guide Modal
const PlatformGuideModal = ({ isOpen, onClose }) => {
    const [activeSection, setActiveSection] = useState(0);

    const sections = [
        {
            icon: <Bot className="text-success" size={24} />,
            title: 'AI Assistant',
            content: 'I\'m your personal onboarding guide! You can ask me questions about your tasks, company policies, or anything else you need help with. I can\'t make changes for you, but I\'ll guide you step-by-step.'
        },
        {
            icon: <Target className="text-blue-400" size={24} />,
            title: 'Your Tasks',
            content: 'Your tasks are organized by phases (Preboarding → Onboarding). Complete them in order, submit any required proofs, and track your progress on the dashboard.'
        },
        {
            icon: <FileText className="text-purple-400" size={24} />,
            title: 'Company Files',
            content: 'Access important documents like your employee handbook, policies, and training materials in the Files section. You can download or view them anytime.'
        },
        {
            icon: <MessageSquare className="text-amber-400" size={24} />,
            title: 'Notices',
            content: 'Stay updated with company announcements in the Notices section. Important updates from your admin will appear here.'
        },
        {
            icon: <Mail className="text-pink-400" size={24} />,
            title: 'Need More Help?',
            content: 'If I can\'t answer your question, just click the "Message Admin" button and I\'ll help you reach out to the right person on your team.'
        }
    ];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 shadow-2xl border border-gray-100 max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                            <BookOpen className="text-success" size={20} />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-800">Platform Guide</h3>
                            <p className="text-xs text-gray-500">Learn how to use envion</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600">
                        <X size={20} />
                    </button>
                </div>

                {/* Section Tabs */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
                    {sections.map((section, idx) => (
                        <button
                            key={idx}
                            onClick={() => setActiveSection(idx)}
                            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all border ${activeSection === idx
                                ? 'bg-primary text-white border-primary shadow-lg shadow-primary/20'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                        >
                            {section.title}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 mb-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-white rounded-lg shadow-sm">
                            {sections[activeSection].icon}
                        </div>
                        <h4 className="text-lg font-bold text-gray-800">{sections[activeSection].title}</h4>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                        {sections[activeSection].content}
                    </p>
                </div>

                {/* Navigation */}
                <div className="flex justify-between items-center">
                    <div className="flex gap-1.5">
                        {sections.map((_, idx) => (
                            <div
                                key={idx}
                                className={`w-2 h-2 rounded-full transition-colors ${activeSection === idx ? 'bg-primary' : 'bg-gray-200'
                                    }`}
                            />
                        ))}
                    </div>
                    <div className="flex gap-2">
                        {activeSection > 0 && (
                            <button
                                onClick={() => setActiveSection(prev => prev - 1)}
                                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-gray-700 text-sm transition-colors font-medium"
                            >
                                Previous
                            </button>
                        )}
                        {activeSection < sections.length - 1 ? (
                            <button
                                onClick={() => setActiveSection(prev => prev + 1)}
                                className="px-4 py-2 bg-primary hover:bg-primary-hover rounded-xl text-white text-sm transition-colors font-medium shadow-md shadow-primary/20"
                            >
                                Next
                            </button>
                        ) : (
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-success hover:bg-success-light rounded-xl text-white text-sm transition-colors font-medium shadow-md shadow-success/20"
                            >
                                Got it!
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Proactive Tip Card Component - Light Theme
const ProactiveTipCard = ({ type, title, content, tasks, onDismiss, onAction, actionLabel }) => {
    const getTypeStyles = () => {
        switch (type) {
            case 'welcome':
                return { bg: 'bg-blue-50', border: 'border-blue-100', icon: <Sparkles className="text-blue-500" size={20} />, titleColor: 'text-blue-800', textColor: 'text-blue-600' };
            case 'struggling':
                return { bg: 'bg-amber-50', border: 'border-amber-100', icon: <AlertTriangle className="text-amber-500" size={20} />, titleColor: 'text-amber-800', textColor: 'text-amber-600' };
            case 'positive':
                return { bg: 'bg-green-50', border: 'border-green-100', icon: <Trophy className="text-green-500" size={20} />, titleColor: 'text-green-800', textColor: 'text-green-600' };
            case 'overdue':
                return { bg: 'bg-red-50', border: 'border-red-100', icon: <Clock className="text-red-500" size={20} />, titleColor: 'text-red-800', textColor: 'text-red-600' };
            default:
                return { bg: 'bg-gray-50', border: 'border-gray-200', icon: <Lightbulb className="text-primary" size={20} />, titleColor: 'text-gray-800', textColor: 'text-gray-600' };
        }
    };

    const styles = getTypeStyles();

    return (
        <div className={`${styles.bg} border ${styles.border} rounded-xl p-4 mb-4 shadow-sm`}>
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">{styles.icon}</div>
                <div className="flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <h4 className={`${styles.titleColor} font-bold text-sm`}>{title}</h4>
                        {onDismiss && (
                            <button onClick={onDismiss} className="text-gray-400 hover:text-gray-600">
                                <X size={16} />
                            </button>
                        )}
                    </div>
                    <p className={`${styles.textColor} text-xs mt-1 leading-relaxed`}>{content}</p>

                    {/* Task List (for welcome/overdue) */}
                    {tasks && tasks.length > 0 && (
                        <div className="mt-3 space-y-2 bg-white/50 rounded-lg p-2">
                            {tasks.map((task, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-xs">
                                    <div className={`w-1.5 h-1.5 rounded-full ${task.status === 'overdue' ? 'bg-red-500' :
                                        task.status === 'pending' ? 'bg-amber-500' : 'bg-gray-400'
                                        }`} />
                                    <span className="text-gray-700 font-medium">{task.title}</span>
                                    {task.dueDate && (
                                        <span className="text-gray-500 ml-auto">• {task.dueDate}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Action Button */}
                    {onAction && actionLabel && (
                        <button
                            onClick={onAction}
                            className="mt-3 flex items-center gap-1.5 text-xs text-primary hover:text-primary-hover font-medium transition-colors"
                        >
                            <span>{actionLabel}</span>
                            <ArrowRight size={12} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// Welcome Back Tip (Top 3 tasks for the week)
const WelcomeBackTip = ({ onDismiss }) => {
    // In production, this would come from API
    const topTasks = [
        { title: 'Complete I-9 Form', dueDate: 'Due Tomorrow', status: 'pending' },
        { title: 'Review Employee Handbook', dueDate: 'Due in 3 days', status: 'pending' },
        { title: 'Set up Direct Deposit', dueDate: 'Due this week', status: 'pending' },
    ];

    return (
        <ProactiveTipCard
            type="welcome"
            title="Welcome Back! 👋"
            content="Here are your top priorities for this week:"
            tasks={topTasks}
            onDismiss={onDismiss}
            onAction={() => window.location.href = '/employee/tasks'}
            actionLabel="View All Tasks"
        />
    );
};

// Struggling Employee Nudge
const StrugglingNudge = ({ onDismiss, onMessageAdmin }) => {
    return (
        <ProactiveTipCard
            type="struggling"
            title="Need Some Help?"
            content="I noticed you might be having trouble with some Day 1 tasks. That's totally okay! Let me know how I can help, or you can reach out to your admin directly."
            onDismiss={onDismiss}
            onAction={onMessageAdmin}
            actionLabel="Message Admin"
        />
    );
};

// Positive Reinforcement
const PositiveReinforcement = ({ onDismiss, completedCount }) => {
    return (
        <ProactiveTipCard
            type="positive"
            title="Great Progress! 🎉"
            content={`You've completed ${completedCount} tasks this week. Keep up the amazing work! You're doing great with your onboarding.`}
            onDismiss={onDismiss}
        />
    );
};

// Overdue Compliance Alert
const OverdueAlert = ({ onDismiss }) => {
    const overdueTasks = [
        { title: 'Complete W-4 Form', dueDate: 'Overdue by 2 days', status: 'overdue' },
        { title: 'Harassment Prevention Training', dueDate: 'Overdue by 1 day', status: 'overdue' },
    ];

    return (
        <ProactiveTipCard
            type="overdue"
            title="Action Required"
            content="You have compliance tasks that need immediate attention:"
            tasks={overdueTasks}
            onDismiss={onDismiss}
            onAction={() => window.location.href = '/employee/tasks'}
            actionLabel="Complete Now"
        />
    );
};

// ============================================
// Phase 9: Rate Limiting & Usage Components
// ============================================

// Usage Dashboard Component - Light Theme
const UsageDashboard = ({ usage, onClose }) => {
    const { usedToday, dailyLimit, remaining } = usage;
    const usagePercent = Math.round((usedToday / dailyLimit) * 100);
    const isWarning = usedToday >= dailyLimit * 0.8;
    const isLimit = remaining <= 0;

    return (
        <div className="bg-card rounded-xl p-4 border border-border shadow-sm">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-foreground flex items-center gap-2">
                    <Target size={16} className="text-primary" />
                    Daily Usage
                </h4>
                {onClose && (
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                        <X size={14} />
                    </button>
                )}
            </div>

            {/* Progress Bar */}
            <div className="h-2 bg-muted rounded-full overflow-hidden mb-2">
                <div
                    className={`h-full transition-all duration-300 ${isLimit ? 'bg-red-500' :
                        isWarning ? 'bg-amber-500' : 'bg-primary'
                        }`}
                    style={{ width: `${Math.min(usagePercent, 100)}%` }}
                />
            </div>

            {/* Stats */}
            <div className="flex justify-between text-xs font-medium">
                <span className="text-muted-foreground">{usedToday} messages sent</span>
                <span className={`${isLimit ? 'text-red-500' : isWarning ? 'text-amber-500' : 'text-muted-foreground'}`}>
                    {remaining > 0 ? `${remaining} remaining` : 'Limit reached'}
                </span>
            </div>

            {/* Warning Messages */}
            {isWarning && !isLimit && (
                <div className="mt-3 py-2 px-3 bg-amber-50 border border-amber-100 rounded-lg">
                    <p className="text-xs text-amber-600">
                        ⚠️ You've used {usagePercent}% of your daily messages. Consider saving some for later!
                    </p>
                </div>
            )}

            {isLimit && (
                <div className="mt-3 py-2 px-3 bg-red-50 border border-red-100 rounded-lg">
                    <p className="text-xs text-red-600">
                        🚫 Daily message limit reached. Your limit resets at midnight UTC.
                    </p>
                </div>
            )}
        </div>
    );
};

// Rate Limit Warning Banner - Light Theme
const RateLimitWarning = ({ usage }) => {
    const { usedToday, dailyLimit, remaining } = usage;
    const isWarning = usedToday >= dailyLimit * 0.8 && remaining > 0;
    const isLimit = remaining <= 0;

    if (!isWarning && !isLimit) return null;

    if (isLimit) {
        return (
            <div className="mx-4 mb-4 py-3 px-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3">
                <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
                <div className="flex-1">
                    <p className="text-sm text-red-700 font-medium">Daily Limit Reached</p>
                    <p className="text-xs text-red-600/70">
                        You've used all {dailyLimit} messages today. Your limit resets at midnight UTC.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-4 mb-4 py-2 px-4 bg-amber-50 border border-amber-100 rounded-xl flex items-center gap-3">
            <AlertTriangle className="text-amber-500 flex-shrink-0" size={18} />
            <p className="text-xs text-amber-700">
                {usedToday} of {dailyLimit} messages used today. {remaining} remaining.
            </p>
        </div>
    );
};

// FileAttachmentModal removed in favor of UnifiedFilePickerModal

// File Processing Progress Component - Light Theme
const FileProcessingProgress = ({ files, currentStep, progress }) => {
    const steps = [
        { id: 1, label: 'Uploading files...' },
        { id: 2, label: 'Extracting content...' },
        { id: 3, label: 'Analyzing document...' },
        { id: 4, label: 'Generating response...' },
    ];

    return (
        <div className="bg-card border border-border rounded-xl p-4 mb-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
                <Loader2 className="animate-spin text-primary" size={18} />
                <span className="text-sm text-foreground font-medium">Processing files...</span>
            </div>

            {/* File List */}
            <div className="flex flex-wrap gap-2 mb-3">
                {files.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 px-2 py-1 bg-muted/50 rounded-lg text-xs border border-border">
                        <span>{getFileIcon(file.name)}</span>
                        <span className="text-muted-foreground truncate max-w-[100px]">{file.name}</span>
                    </div>
                ))}
            </div>

            {/* Progress Steps */}
            <div className="space-y-2">
                {steps.map((step) => (
                    <div key={step.id} className="flex items-center gap-2">
                        {currentStep > step.id ? (
                            <Check size={14} className="text-success" />
                        ) : currentStep === step.id ? (
                            <Loader2 size={14} className="animate-spin text-primary" />
                        ) : (
                            <div className="w-3.5 h-3.5 rounded-full border border-border" />
                        )}
                        <span className={`text-xs ${currentStep >= step.id ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                            {step.label}
                        </span>
                    </div>
                ))}
            </div>

            {/* Progress Bar */}
            <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );
};

// Source Citation Component - Enhanced for Phase 5
const SourceCitation = ({ citations }) => {
    // Handle both old format (array) and new format (object with categorized citations)
    if (!citations) return null;

    // Convert to new format if old array format
    const sources = Array.isArray(citations)
        ? citations.map(c => typeof c === 'string' ? { type: 'file', name: c } : c)
        : citations.all || [];

    if (sources.length === 0) return null;

    const getSourceIcon = (type) => {
        switch (type) {
            case 'task': return '📋';
            case 'file': return '📄';
            case 'notice': return '📢';
            case 'general': return '💡';
            default: return '📎';
        }
    };

    const getSourceColor = (type) => {
        switch (type) {
            case 'task': return 'bg-blue-50 text-blue-600 border-blue-100';
            case 'file': return 'bg-primary/5 text-primary border-primary/20';
            case 'notice': return 'bg-amber-50 text-amber-600 border-amber-100';
            case 'general': return 'bg-muted text-muted-foreground border-border';
            default: return 'bg-muted text-muted-foreground border-border';
        }
    };

    const getSourceLabel = (type) => {
        switch (type) {
            case 'task': return 'Task';
            case 'file': return 'File';
            case 'notice': return 'Notice';
            case 'general': return 'General Knowledge';
            default: return 'Source';
        }
    };

    // Check if it's just general knowledge (special styling)
    const isOnlyGeneral = sources.length === 1 && sources[0].type === 'general';

    if (isOnlyGeneral) {
        return (
            <div className="mt-3 pt-3 border-t border-border/50">
                <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg border border-border">
                    <span className="text-base">💡</span>
                    <div>
                        <span className="text-xs text-foreground font-medium">General Knowledge</span>
                        <p className="text-xs text-muted-foreground">Please verify with your manager or handbook</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-3 pt-3 border-t border-border/50">
            <p className="text-xs text-muted-foreground mb-2">Sources:</p>
            <div className="flex flex-wrap gap-2">
                {sources.map((source, idx) => (
                    <div
                        key={idx}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border ${getSourceColor(source.type)}`}
                    >
                        <span className="text-sm">{getSourceIcon(source.type)}</span>
                        <span className="font-medium">{source.name}</span>
                        {source.page && (
                            <span className="text-muted-foreground ml-1">p.{source.page}</span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

// Fullscreen Panel Wrapper Component - Light Theme
const FullscreenPanel = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-white animate-in fade-in duration-200">
            <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-card border-b border-border shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Expand className="text-primary" size={16} />
                        </div>
                        <span className="text-foreground font-bold">{title}</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 px-3 py-2 bg-muted hover:bg-muted/80 rounded-lg text-muted-foreground text-sm transition-colors font-medium"
                    >
                        <Minimize2 size={16} />
                        Exit Fullscreen
                    </button>
                </div>
                {/* Content */}
                <div className="flex-1 overflow-hidden bg-background">
                    {children}
                </div>
            </div>
        </div>
    );
};

// Markdown prose styles for document rendering
const markdownComponents = {
    h1: ({ children }) => <h1 className="text-2xl font-bold text-gray-900 mb-4 mt-6 pb-2 border-b border-gray-200">{children}</h1>,
    h2: ({ children }) => <h2 className="text-xl font-semibold text-gray-800 mb-3 mt-5">{children}</h2>,
    h3: ({ children }) => <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-4">{children}</h3>,
    h4: ({ children }) => <h4 className="text-base font-semibold text-gray-700 mb-2 mt-3">{children}</h4>,
    p: ({ children }) => <p className="text-gray-700 leading-relaxed mb-3">{children}</p>,
    ul: ({ children }) => <ul className="list-disc list-inside space-y-1.5 mb-3 text-gray-700 ml-2">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-inside space-y-1.5 mb-3 text-gray-700 ml-2">{children}</ol>,
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    blockquote: ({ children }) => <blockquote className="border-l-4 border-primary/30 bg-primary/5 pl-4 py-2 my-3 text-gray-600 italic rounded-r-lg">{children}</blockquote>,
    code: ({ inline, children, ...props }) => inline
        ? <code className="bg-gray-100 text-primary px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
        : <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg my-3 overflow-x-auto text-sm font-mono"><code {...props}>{children}</code></pre>,
    table: ({ children }) => <div className="overflow-x-auto my-3"><table className="w-full border-collapse border border-gray-200 rounded-lg overflow-hidden">{children}</table></div>,
    thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
    th: ({ children }) => <th className="border border-gray-200 px-3 py-2 text-left text-sm font-semibold text-gray-700">{children}</th>,
    td: ({ children }) => <td className="border border-gray-200 px-3 py-2 text-sm text-gray-600">{children}</td>,
    a: ({ children, href }) => <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
    hr: () => <hr className="my-4 border-gray-200" />,
    strong: ({ children }) => <strong className="font-semibold text-gray-800">{children}</strong>,
    em: ({ children }) => <em className="italic text-gray-600">{children}</em>,
};

// Compact markdown styles for chat messages
const chatMarkdownComponents = {
    ...markdownComponents,
    h1: ({ children }) => <h1 className="text-lg font-bold text-gray-900 mb-2 mt-3 pb-1 border-b border-gray-200">{children}</h1>,
    h2: ({ children }) => <h2 className="text-base font-semibold text-gray-800 mb-2 mt-3">{children}</h2>,
    h3: ({ children }) => <h3 className="text-sm font-semibold text-gray-800 mb-1.5 mt-2">{children}</h3>,
    p: ({ children }) => <p className="text-sm text-gray-700 leading-relaxed mb-2">{children}</p>,
    ul: ({ children }) => <ul className="list-disc list-inside space-y-1 mb-2 text-sm text-gray-700 ml-1">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 mb-2 text-sm text-gray-700 ml-1">{children}</ol>,
    li: ({ children }) => <li className="leading-relaxed text-sm">{children}</li>,
    code: ({ inline, children, ...props }) => inline
        ? <code className="bg-gray-100 text-primary px-1 py-0.5 rounded text-xs font-mono">{children}</code>
        : <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg my-2 overflow-x-auto text-xs font-mono"><code {...props}>{children}</code></pre>,
};

// Document Editor Component - Professional
const DocumentEditor = ({
    document,
    onClose,
    onSave,
    onDownload,
    isFullscreen,
    onToggleFullscreen,
    updateStatus,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    editorContent,
    setEditorContent
}) => {
    const [editorMode, setEditorMode] = useState('preview'); // 'edit' | 'preview'

    const handleCopyContent = () => {
        navigator.clipboard.writeText(editorContent);
        toast.success('Content copied to clipboard!');
    };

    // Word & character stats
    const stats = useMemo(() => {
        const text = editorContent || '';
        const words = text.trim() ? text.trim().split(/\s+/).length : 0;
        const chars = text.length;
        const readingTime = Math.max(1, Math.ceil(words / 200));
        return { words, chars, readingTime };
    }, [editorContent]);

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Editor Header */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-200">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <FileText className="text-primary" size={18} />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="text-gray-900 font-bold truncate max-w-[180px] sm:max-w-[300px]">
                                {document?.title || 'Generated Document'}
                            </span>
                            {hasUnsavedChanges && (
                                <span className="px-2 py-0.5 text-xs bg-amber-50 text-amber-600 rounded-full font-medium border border-amber-200 animate-pulse">Unsaved</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5">
                    {updateStatus && (
                        <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${updateStatus === 'updating'
                            ? 'bg-primary/5 text-primary'
                            : 'bg-green-50 text-green-600'
                            }`}>
                            {updateStatus === 'updating' ? (
                                <><RefreshCw className="animate-spin" size={13} /><span className="hidden sm:inline">Saving...</span></>
                            ) : (
                                <><Check size={13} /><span className="hidden sm:inline">Saved</span></>
                            )}
                        </div>
                    )}

                    {/* Edit/Preview Toggle */}
                    <div className="flex items-center p-0.5 bg-gray-100 rounded-lg border border-gray-200">
                        <button
                            onClick={() => setEditorMode('edit')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${editorMode === 'edit'
                                ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <Pencil size={13} />
                            <span className="hidden lg:inline">Edit</span>
                        </button>
                        <button
                            onClick={() => setEditorMode('preview')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${editorMode === 'preview'
                                ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <Eye size={13} />
                            <span className="hidden lg:inline">Preview</span>
                        </button>
                    </div>

                    <div className="w-px h-5 bg-gray-200 mx-0.5" />

                    {/* Action Buttons */}
                    <button
                        onClick={handleCopyContent}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                        title="Copy content"
                    >
                        <Copy size={16} />
                    </button>
                    <button
                        onClick={onDownload}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                        title="Download"
                    >
                        <Download size={16} />
                    </button>
                    <button
                        onClick={onSave}
                        className="flex items-center gap-1.5 px-3 py-2 bg-primary hover:bg-primary-hover rounded-lg text-white text-xs font-medium transition-all shadow-sm hover:shadow-md hover:shadow-primary/20"
                        title="Save to My Files"
                    >
                        <Save size={14} />
                        <span className="hidden lg:inline">Save</span>
                    </button>

                    <div className="w-px h-5 bg-gray-200 mx-0.5" />

                    <button
                        onClick={onToggleFullscreen}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                        title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                    >
                        {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                    </button>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500 transition-colors"
                        title="Close editor"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* Editor Content Area */}
            <div className="flex-1 overflow-auto bg-gray-50">
                {editorMode === 'edit' ? (
                    <div className="h-full p-4 sm:p-6 lg:p-8">
                        <div className="max-w-4xl mx-auto h-full">
                            <textarea
                                value={editorContent}
                                onChange={(e) => {
                                    setEditorContent(e.target.value);
                                    setHasUnsavedChanges(true);
                                }}
                                className="w-full h-full min-h-[500px] bg-white border border-gray-200 rounded-xl p-6 sm:p-8 text-gray-800 text-sm leading-relaxed resize-none focus:outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/10 transition-all placeholder:text-gray-400 shadow-sm font-mono"
                                placeholder="Write or edit your document content here..."
                                spellCheck={true}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="h-full p-4 sm:p-6 lg:p-8">
                        <div className="max-w-4xl mx-auto bg-white rounded-xl border border-gray-200 shadow-sm min-h-[500px]">
                            {/* Document paper styling */}
                            <div className="p-6 sm:p-8 lg:p-10">
                                {editorContent ? (
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={markdownComponents}
                                    >
                                        {editorContent}
                                    </ReactMarkdown>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                                        <FileText size={40} strokeWidth={1.5} />
                                        <p className="mt-3 text-sm">No content yet. Switch to Edit mode to start writing.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer - Word count & stats */}
            <div className="flex items-center justify-between px-4 py-2 bg-white border-t border-gray-200 text-xs text-gray-400">
                <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5">
                        <Type size={12} />
                        {stats.words.toLocaleString()} words
                    </span>
                    <span className="flex items-center gap-1.5">
                        <Hash size={12} />
                        {stats.chars.toLocaleString()} chars
                    </span>
                    <span className="flex items-center gap-1.5">
                        <Clock size={12} />
                        {stats.readingTime} min read
                    </span>
                </div>
                <span className="flex items-center gap-1.5">
                    <AlignLeft size={12} />
                    {editorMode === 'edit' ? 'Markdown' : 'Preview'}
                </span>
            </div>
        </div>
    );
};

const EmployeeAIPage = () => {
    // Navigation
    const navigate = useNavigate();

    // State - Conversations
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [usage, setUsage] = useState({ usedToday: 0, dailyLimit: 100, remaining: 100 });
    const [activeCount, setActiveCount] = useState(0);
    const [maxAllowed, setMaxAllowed] = useState(5);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);


    // State - Document Editor (Phase 2)
    const [activeDocument, setActiveDocument] = useState(null);
    const [editorContent, setEditorContent] = useState('');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
    const [isChatFullscreen, setIsChatFullscreen] = useState(false);
    const [splitPosition, setSplitPosition] = useState(40); // 40% chat, 60% editor
    const [isDragging, setIsDragging] = useState(false);
    const [updateStatus, setUpdateStatus] = useState(null); // 'updating' | 'updated' | null

    // Modals
    const [showFormatModal, setShowFormatModal] = useState(false);
    const [showSaveAsModal, setShowSaveAsModal] = useState(false);
    const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
    const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
    const [showFileAttachmentModal, setShowFileAttachmentModal] = useState(false);
    const [isSavingDocument, setIsSavingDocument] = useState(false);

    // State - File Upload (Phase 3)
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [isProcessingFiles, setIsProcessingFiles] = useState(false);
    const [fileProcessingStep, setFileProcessingStep] = useState(0);
    const [fileProcessingProgress, setFileProcessingProgress] = useState(0);

    // State - Admin Messaging (Phase 6)
    const [showAdminMessageModal, setShowAdminMessageModal] = useState(false);
    const [adminMessageContext, setAdminMessageContext] = useState('');

    // State - Proactive Help (Phase 7)
    const [showPlatformGuide, setShowPlatformGuide] = useState(false);
    const [proactiveTips, setProactiveTips] = useState({
        showWelcome: true, // Show on first visit of the day
        showStruggling: false, // Show if Day 1 tasks incomplete by Day 3
        showPositive: false, // Show after completing multiple tasks
        showOverdue: false, // Show if overdue compliance tasks
        completedThisWeek: 3, // Mock value - would come from API
    });
    // Refs
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const splitContainerRef = useRef(null);

    // ============================================
    // Effects
    // ============================================

    useEffect(() => {
        fetchConversations();
        fetchUsageStats();

        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            if (window.innerWidth < 768) {
                setSidebarCollapsed(true);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
        }
    }, [inputMessage]);

    // Handle split resize
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging || !splitContainerRef.current) return;

            const container = splitContainerRef.current;
            const containerRect = container.getBoundingClientRect();
            const newPosition = ((e.clientX - containerRect.left) / containerRect.width) * 100;

            // Clamp between 25% and 75%
            setSplitPosition(Math.min(75, Math.max(25, newPosition)));
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    // ============================================
    // API Functions
    // ============================================

    const fetchConversations = async () => {
        try {
            setIsLoading(true);
            const response = await api.get('/employee/ai/conversations');
            setConversations(response.data.conversations || []);
            setActiveCount(response.data.activeCount || 0);
            setMaxAllowed(response.data.maxAllowed || 5);
        } catch (error) {
            console.error('Error fetching conversations:', error);
            toast.error('Failed to load conversations');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchUsageStats = async () => {
        try {
            const response = await api.get('/employee/ai/usage');
            setUsage(response.data);
        } catch (error) {
            console.error('Error fetching usage:', error);
        }
    };

    const createConversation = async () => {
        if (activeCount >= maxAllowed) {
            toast.error(`Maximum ${maxAllowed} conversations allowed. Please archive an existing one.`);
            return;
        }

        try {
            const response = await api.post('/employee/ai/conversations');
            const newConv = response.data.conversation;
            setConversations(prev => [newConv, ...prev]);
            setActiveConversation(newConv);
            setMessages([]);
            setActiveCount(response.data.activeCount);
            if (isMobile) setSidebarCollapsed(true);
        } catch (error) {
            console.error('Error creating conversation:', error);
            toast.error(error.response?.data?.error || 'Failed to create conversation');
        }
    };

    const loadConversation = async (conversation) => {
        try {
            setIsLoading(true);
            const response = await api.get(`/employee/ai/conversations/${conversation.id}`);
            setActiveConversation(response.data.conversation);
            setMessages(response.data.messages || []);
            if (isMobile) setSidebarCollapsed(true);
        } catch (error) {
            console.error('Error loading conversation:', error);
            toast.error('Failed to load conversation');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleStar = async (e, conversationId) => {
        e.stopPropagation();
        try {
            const response = await api.patch(`/employee/ai/conversations/${conversationId}/star`);
            setConversations(prev =>
                prev.map(c => c.id === conversationId ? response.data.conversation : c)
            );
        } catch (error) {
            console.error('Error toggling star:', error);
            toast.error('Failed to update');
        }
    };

    const archiveConversation = async (e, conversationId) => {
        e.stopPropagation();
        try {
            await api.patch(`/employee/ai/conversations/${conversationId}/archive`);
            setConversations(prev => prev.filter(c => c.id !== conversationId));
            if (activeConversation?.id === conversationId) {
                setActiveConversation(null);
                setMessages([]);
            }
            setActiveCount(prev => prev - 1);
            toast.success('Conversation archived');
        } catch (error) {
            console.error('Error archiving conversation:', error);
            toast.error('Failed to archive');
        }
    };

    const sendMessage = async (e) => {
        e?.preventDefault();
        if (!inputMessage.trim() || isSending || !activeConversation) return;

        if (usage.remaining <= 0) {
            toast.error('Daily message limit reached. Try again after midnight.');
            return;
        }

        const userMessage = inputMessage.trim();
        setInputMessage('');
        setIsSending(true);

        const tempUserMsg = {
            id: `temp-${Date.now()}`,
            role: 'employee',
            content: userMessage,
            created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, tempUserMsg]);

        try {
            const response = await api.post(`/employee/ai/conversations/${activeConversation.id}/messages`, {
                message: userMessage,
                useFileSearch: true // Always search company files
            });

            setMessages(prev => {
                const filtered = prev.filter(m => m.id !== tempUserMsg.id);
                return [...filtered, response.data.employeeMessage, response.data.aiMessage];
            });

            if (response.data.usage) {
                setUsage(response.data.usage);
            }

            // Check if AI generated a document
            const aiMessage = response.data.aiMessage;
            if (aiMessage?.metadata?.document) {
                // Backend provided structured document metadata
                setActiveDocument(aiMessage.metadata.document);
                setEditorContent(aiMessage.metadata.document.content || '');
                setHasUnsavedChanges(false);
            } else if (aiMessage?.content) {
                // Frontend document detection: Check for document patterns
                const content = aiMessage.content;
                const isGeneratedDocument = content.includes('[Generated Document') ||
                    content.includes('[Generated Document]');
                const hasDocumentStructure = (
                    content.includes('# ') && // Has headings
                    content.includes('## ') && // Has subheadings
                    content.split('\n').length > 20 // Is substantial length
                );

                if (isGeneratedDocument || hasDocumentStructure) {
                    // Extract title from first heading or use default
                    const titleMatch = content.match(/^#\s+(.+)$/m);
                    const documentTitle = titleMatch ? titleMatch[1] : 'Generated Document';

                    // Clean up the content (remove citation tag if at the end)
                    const cleanedContent = content.replace(/\[Generated Document.*?\]$/s, '').trim();

                    setActiveDocument({
                        title: documentTitle,
                        content: cleanedContent,
                        createdAt: new Date().toISOString()
                    });
                    setEditorContent(cleanedContent);
                    setHasUnsavedChanges(false);
                    toast.success('Document opened in editor!');
                }
            }

            if (activeConversation.title === 'New Chat') {
                fetchConversations();
            }

        } catch (error) {
            console.error('Error sending message:', error);
            setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));

            if (error.response?.status === 429) {
                toast.error('Daily limit reached. Try again tomorrow.');
                setUsage(error.response.data);
            } else {
                toast.error(error.response?.data?.error || 'Failed to send message');
            }
        } finally {
            setIsSending(false);
        }
    };

    // ============================================
    // File Upload Functions (Phase 3)
    // ============================================

    const handleFilesSelected = (files, type) => {
        if (type === 'upload') {
            // Validate uploaded files
            const validFiles = [];
            const errors = [];
            files.forEach(file => {
                const validation = validateFile(file);
                if (validation.valid) {
                    validFiles.push(file);
                } else {
                    errors.push(`${file.name}: ${validation.error}`);
                }
            });

            if (errors.length > 0) errors.forEach(err => toast.error(err));
            if (validFiles.length > 0) {
                setAttachedFiles(prev => [...prev, ...validFiles]);
                toast.success(`${validFiles.length} file(s) attached`);
            }
        } else if (type === 'library') {
            // For library files, we append a request to analyze them in the chat
            const fileNames = files.map(f => `"${f.file_name}"`).join(', ');
            const prompt = `Please analyze the document(s): ${fileNames}`;
            setInputMessage(prev => prev ? `${prev}\n${prompt}` : prompt);
            toast.success(`Added ${files.length} document(s) to context`);
        }
    };

    const removeAttachedFile = (index) => {
        setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const sendMessageWithFiles = async () => {
        if ((!inputMessage.trim() && attachedFiles.length === 0) || isSending || !activeConversation) return;

        if (usage.remaining <= 0) {
            toast.error('Daily message limit reached. Try again after midnight.');
            return;
        }

        const userMessage = inputMessage.trim();
        const filesToProcess = [...attachedFiles];

        setInputMessage('');
        setAttachedFiles([]);
        setIsSending(true);

        // If we have files, show processing UI
        if (filesToProcess.length > 0) {
            setIsProcessingFiles(true);
            setFileProcessingStep(1);
            setFileProcessingProgress(0);
        }

        // Create temp message with files info
        const tempUserMsg = {
            id: `temp-${Date.now()}`,
            role: 'employee',
            content: userMessage,
            attachments: filesToProcess.map(f => ({ name: f.name, size: f.size })),
            created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, tempUserMsg]);

        try {
            // Simulate file processing steps
            if (filesToProcess.length > 0) {
                // Step 1: Uploading
                setFileProcessingStep(1);
                setFileProcessingProgress(20);
                await new Promise(r => setTimeout(r, 500));

                // Step 2: Extracting
                setFileProcessingStep(2);
                setFileProcessingProgress(45);
                await new Promise(r => setTimeout(r, 500));

                // Step 3: Analyzing
                setFileProcessingStep(3);
                setFileProcessingProgress(70);
                await new Promise(r => setTimeout(r, 500));

                // Step 4: Generating
                setFileProcessingStep(4);
                setFileProcessingProgress(90);
            }

            // Convert files to base64 for sending
            let fileData = [];
            if (filesToProcess.length > 0) {
                fileData = await Promise.all(
                    filesToProcess.map(async (file) => {
                        return new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onload = () => {
                                resolve({
                                    name: file.name,
                                    type: file.type,
                                    size: file.size,
                                    data: reader.result.split(',')[1] // base64 data
                                });
                            };
                            reader.readAsDataURL(file);
                        });
                    })
                );
            }

            // Send message with file data
            const response = await api.post(`/employee/ai/conversations/${activeConversation.id}/messages`, {
                message: userMessage || `Please analyze the attached file(s)`,
                files: fileData.length > 0 ? fileData : undefined
            });

            setFileProcessingProgress(100);

            // Update messages with attachments info
            const updatedEmployeeMsg = {
                ...response.data.employeeMessage,
                attachments: filesToProcess.map(f => ({ name: f.name, size: f.size, type: f.type }))
            };

            setMessages(prev => {
                const filtered = prev.filter(m => m.id !== tempUserMsg.id);
                return [...filtered, updatedEmployeeMsg, response.data.aiMessage];
            });

            if (response.data.usage) {
                setUsage(response.data.usage);
            }

            // Check if AI generated a document or file summary
            const aiMessage = response.data.aiMessage;
            if (aiMessage?.metadata?.document || (fileData.length > 0 && aiMessage?.metadata?.fileSummary)) {
                const doc = aiMessage.metadata.document || {
                    title: `Analysis of ${filesToProcess[0]?.name || 'uploaded file'}`,
                    content: aiMessage.metadata.fileSummary || aiMessage.content
                };
                setActiveDocument(doc);
                setEditorContent(doc.content || '');
                setHasUnsavedChanges(false);
            }

            if (activeConversation.title === 'New Chat') {
                fetchConversations();
            }

        } catch (error) {
            console.error('Error sending message:', error);
            setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
            setAttachedFiles(filesToProcess); // Restore files on error

            if (error.response?.status === 429) {
                toast.error('Daily limit reached. Try again tomorrow.');
                setUsage(error.response.data);
            } else {
                toast.error(error.response?.data?.error || 'Failed to send message');
            }
        } finally {
            setIsSending(false);
            setIsProcessingFiles(false);
            setFileProcessingStep(0);
            setFileProcessingProgress(0);
        }
    };

    // ============================================
    // Document Editor Functions
    // ============================================

    const handleCloseEditor = () => {
        if (hasUnsavedChanges) {
            setShowUnsavedWarning(true);
        } else {
            closeEditor();
        }
    };

    const closeEditor = () => {
        setActiveDocument(null);
        setEditorContent('');
        setHasUnsavedChanges(false);
        setIsEditorFullscreen(false);
        setShowUnsavedWarning(false);
    };

    const handleDownload = () => {
        setShowFormatModal(true);
    };

    const handleFormatSelect = async (format) => {
        setShowFormatModal(false);

        // Create blob based on format
        let blob;
        const filename = `${activeDocument?.title || 'document'}.${format}`;

        if (format === 'txt') {
            blob = new Blob([editorContent], { type: 'text/plain' });
        } else {
            // For other formats, we'd need backend support
            // For now, download as txt with appropriate extension
            blob = new Blob([editorContent], { type: 'text/plain' });
            toast.success(`Downloaded as ${format} (text format)`);
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success('Document downloaded!');
    };

    const handleSaveToFiles = () => {
        if (!activeDocument || !editorContent) {
            toast.error('No document to save');
            return;
        }
        setShowSaveAsModal(true);
    };

    const handleSaveWithName = async (fileName) => {
        try {
            setIsSavingDocument(true);
            setUpdateStatus('updating');

            const response = await api.post('/employee/ai/save-document', {
                title: fileName,
                content: editorContent,
                format: 'txt'
            });

            if (response.data.success) {
                setUpdateStatus('updated');
                setHasUnsavedChanges(false);
                setShowSaveAsModal(false);
                setShowSaveConfirmModal(true);
                // Update the document title in the editor
                setActiveDocument(prev => ({ ...prev, title: fileName }));
                setTimeout(() => setUpdateStatus(null), 2000);
            } else {
                throw new Error(response.data.error || 'Failed to save');
            }
        } catch (error) {
            console.error('Error saving:', error);
            toast.error(error.response?.data?.error || 'Failed to save document');
            setUpdateStatus(null);
        } finally {
            setIsSavingDocument(false);
        }
    };

    const handleViewInFiles = () => {
        setShowSaveConfirmModal(false);
        // Navigate to files page - in production, use router
        window.location.href = '/employee/files';
    };

    const handleDownloadAlso = () => {
        setShowSaveConfirmModal(false);
        handleDownload();
    };

    // ============================================
    // Helpers
    // ============================================

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const today = new Date();
        if (date.toDateString() === today.toDateString()) return 'Today';
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
        return date.toLocaleDateString();
    };

    const filteredConversations = conversations.filter(conv =>
        conv.title?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Calculate layout widths
    const chatWidth = isChatFullscreen
        ? 100
        : (activeDocument
            ? (isEditorFullscreen ? 0 : splitPosition)
            : 100);

    const editorWidth = isChatFullscreen
        ? 0
        : (activeDocument
            ? (isEditorFullscreen ? 100 : 100 - splitPosition)
            : 0);

    // ============================================
    // Render
    // ============================================

    return (
        <div className="flex h-screen w-full bg-card text-foreground overflow-hidden">
            {/* Sidebar - Enhanced mobile drawer */}
            {isMobile && !sidebarCollapsed && (
                <div
                    className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
                    onClick={() => setSidebarCollapsed(true)}
                />
            )}
            <aside className={`
                ${sidebarCollapsed ? (isMobile ? 'w-0 min-w-0' : 'w-[70px] min-w-[70px]') : 'w-[280px] sm:w-[20%] sm:min-w-[280px] sm:max-w-[320px]'}
                bg-secondary flex flex-col border-r border-border transition-all duration-300 overflow-hidden
                ${isMobile ? 'fixed left-0 top-0 bottom-0 z-50 shadow-2xl' : ''}
                ${isMobile && sidebarCollapsed ? '-translate-x-full' : 'translate-x-0'}
            `}>
                <div className={`flex items-center ${sidebarCollapsed && !isMobile ? 'justify-center' : 'justify-between'} p-4 border-b border-border`}>
                    {!sidebarCollapsed && (
                        <div className="flex items-center gap-2 text-primary font-bold">
                            <Bot size={20} />
                            <span>AI Assistant</span>
                        </div>
                    )}
                    {sidebarCollapsed && !isMobile && (
                        <div className="flex items-center justify-center text-primary font-bold">
                            <Bot size={24} />
                        </div>
                    )}

                    <button
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        className={`p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors ${sidebarCollapsed && !isMobile ? 'hidden' : ''}`}
                    >
                        {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                    </button>
                    {!sidebarCollapsed && isMobile && (
                        <button
                            onClick={() => setSidebarCollapsed(true)}
                            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <ChevronLeft size={18} />
                        </button>
                    )}
                </div>

                <div className="flex items-center justify-center py-2">
                    <button
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        className={`p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors ${!sidebarCollapsed || isMobile ? 'hidden' : 'block'}`}
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>

                {!sidebarCollapsed && (
                    <>
                        <button
                            onClick={createConversation}
                            disabled={activeCount >= maxAllowed}
                            className="flex items-center justify-center gap-2 mx-4 mt-4 py-3 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity shadow-sm"
                        >
                            <Plus size={18} />
                            <span>New Chat</span>
                        </button>

                        <div className="text-center text-xs text-muted-foreground mt-2 mb-3">
                            {activeCount} of {maxAllowed} conversations
                        </div>

                        <div className="flex items-center gap-2 mx-4 mb-3 px-3 py-2 bg-card rounded-lg border border-border shadow-sm focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                            <Search size={16} className="text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Search conversations..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="flex-1 bg-transparent border-none text-sm text-foreground placeholder-muted-foreground focus:outline-none"
                            />
                        </div>

                        <div className="flex-1 overflow-y-auto px-2">
                            {isLoading && conversations.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                                    <Loader2 className="animate-spin" size={20} />
                                    <span className="mt-2 text-sm">Loading...</span>
                                </div>
                            ) : filteredConversations.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                                    <MessageSquare size={24} />
                                    <p className="mt-2 text-sm">No conversations yet</p>
                                </div>
                            ) : (
                                filteredConversations.map((conv) => (
                                    <div
                                        key={conv.id}
                                        onClick={() => loadConversation(conv)}
                                        className={`
                                            flex items-center p-3 mb-1 rounded-lg cursor-pointer transition-colors group
                                            ${activeConversation?.id === conv.id
                                                ? 'bg-primary/10 border-l-3 border-primary'
                                                : 'hover:bg-muted'}
                                        `}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className={`text-sm font-medium truncate ${activeConversation?.id === conv.id ? 'text-primary' : 'text-foreground'}`}>
                                                {conv.title}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                {formatDate(conv.updated_at)}
                                            </div>
                                        </div>
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={(e) => toggleStar(e, conv.id)}
                                                className={`p-1 rounded hover:bg-card transition-colors ${conv.is_starred ? 'text-yellow-400' : 'text-muted-foreground'}`}
                                            >
                                                <Star size={14} />
                                            </button>
                                            <button
                                                onClick={(e) => archiveConversation(e, conv.id)}
                                                className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"
                                            >
                                                <Archive size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="flex items-center justify-center gap-2 p-3 text-xs text-muted-foreground border-t border-border">
                            <span>{usage.remaining} / {usage.dailyLimit} messages left</span>
                            {usage.remaining <= 20 && <AlertCircle size={14} className="text-amber-500" />}
                        </div>
                    </>
                )}
            </aside>

            {/* Main Content Area */}
            <main
                ref={splitContainerRef}
                className="flex-1 flex overflow-hidden min-h-0"
            >
                {/* Chat Area */}
                <div
                    className={`flex flex-col bg-background overflow-hidden min-h-0 transition-all duration-300 ${isChatFullscreen ? 'fixed inset-0 z-50' : ''}`}
                    style={{ width: isChatFullscreen ? '100%' : `${chatWidth}%` }}
                >
                    {!activeConversation ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-card">
                            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                                <Bot size={48} className="text-primary" />
                            </div>
                            <h2 className="text-2xl font-bold text-foreground mb-3">Employee AI Assistant</h2>
                            <p className="max-w-md text-muted-foreground leading-relaxed mb-8">
                                I'm here to help you with your onboarding journey. Ask me about your tasks,
                                company policies, or anything else you need help with.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <button
                                    onClick={createConversation}
                                    className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary-hover text-white rounded-xl font-medium hover:shadow-lg hover:shadow-primary/30 transition-all"
                                >
                                    <Plus size={18} />
                                    Start a New Chat
                                </button>
                                <button
                                    onClick={() => setShowPlatformGuide(true)}
                                    className="flex items-center gap-2 px-6 py-3 bg-card border border-border hover:bg-muted text-foreground rounded-xl font-medium transition-all shadow-sm"
                                >
                                    <HelpCircle size={18} />
                                    Platform Guide
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Chat Header */}
                            <div className="flex items-center justify-between px-4 py-3 bg-card/80 backdrop-blur-md border-b border-border sticky top-0 z-10">
                                <div className="flex items-center gap-4">
                                    {isMobile && (
                                        <button
                                            onClick={() => setSidebarCollapsed(false)}
                                            className="p-2 hover:bg-muted rounded-lg text-muted-foreground"
                                        >
                                            <MessageSquare size={20} />
                                        </button>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <Bot size={18} className="text-primary" />
                                        </div>
                                        <span className="text-foreground font-bold truncate">
                                            {activeConversation.title}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => {
                                            setIsChatFullscreen(!isChatFullscreen);
                                            if (!isChatFullscreen) setIsEditorFullscreen(false);
                                        }}
                                        className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                                        title={isChatFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                                    >
                                        {isChatFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                                    </button>
                                    <button
                                        onClick={() => setShowPlatformGuide(true)}
                                        className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                                        title="Platform Guide"
                                    >
                                        <HelpCircle size={18} />
                                    </button>
                                </div>
                            </div>

                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {/* Proactive Tips - Phase 7 */}
                                {messages.length === 0 && (
                                    <div className="mb-4">
                                        {proactiveTips.showWelcome && (
                                            <WelcomeBackTip
                                                onDismiss={() => setProactiveTips(prev => ({ ...prev, showWelcome: false }))}
                                            />
                                        )}
                                        {proactiveTips.showStruggling && (
                                            <StrugglingNudge
                                                onDismiss={() => setProactiveTips(prev => ({ ...prev, showStruggling: false }))}
                                                onMessageAdmin={() => {
                                                    setAdminMessageContext('I need help with my Day 1 tasks');
                                                    setShowAdminMessageModal(true);
                                                }}
                                            />
                                        )}
                                        {proactiveTips.showPositive && (
                                            <PositiveReinforcement
                                                onDismiss={() => setProactiveTips(prev => ({ ...prev, showPositive: false }))}
                                                completedCount={proactiveTips.completedThisWeek}
                                            />
                                        )}
                                        {proactiveTips.showOverdue && (
                                            <OverdueAlert
                                                onDismiss={() => setProactiveTips(prev => ({ ...prev, showOverdue: false }))}
                                            />
                                        )}
                                    </div>
                                )}

                                {messages.length === 0 ? (
                                    <div className="flex-1 flex flex-col items-center justify-center px-4">
                                        <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                                            <Sparkles size={28} className="text-primary" />
                                        </div>
                                        <h3 className="text-lg font-bold text-foreground mb-1">How can I help you?</h3>
                                        <p className="text-sm text-muted-foreground mb-6">Ask me anything or try one of these suggestions</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full max-w-lg">
                                            {[
                                                { icon: <Target size={16} className="text-blue-500" />, label: 'What are my pending tasks?', bg: 'bg-blue-50 hover:bg-blue-100 border-blue-100' },
                                                { icon: <BookOpen size={16} className="text-purple-500" />, label: 'Explain company policies', bg: 'bg-purple-50 hover:bg-purple-100 border-purple-100' },
                                                { icon: <FileText size={16} className="text-green-500" />, label: 'Generate a document for me', bg: 'bg-green-50 hover:bg-green-100 border-green-100' },
                                                { icon: <HelpCircle size={16} className="text-amber-500" />, label: 'Help with my onboarding', bg: 'bg-amber-50 hover:bg-amber-100 border-amber-100' },
                                            ].map((suggestion, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => {
                                                        setInputMessage(suggestion.label);
                                                        textareaRef.current?.focus();
                                                    }}
                                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm text-gray-700 font-medium border transition-all ${suggestion.bg} hover:shadow-sm`}
                                                >
                                                    {suggestion.icon}
                                                    <span>{suggestion.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    messages.map((msg) => (
                                        <div
                                            key={msg.id}
                                            className={`flex gap-3 max-w-[85%] ${msg.role === 'employee' ? 'ml-auto flex-row-reverse' : ''}`}
                                        >
                                            {msg.role === 'assistant' && (
                                                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0 shadow-sm border border-white">
                                                    <Bot size={16} className="text-white" />
                                                </div>
                                            )}
                                            <div className={`
                                                px-4 py-3 rounded-2xl max-w-full shadow-sm
                                                ${msg.role === 'employee'
                                                    ? 'bg-primary text-white rounded-br-md shadow-primary/20'
                                                    : 'bg-card border border-border text-foreground rounded-bl-md'}
                                            `}>
                                                <div className="text-sm leading-relaxed break-words">
                                                    {msg.role === 'assistant' && msg.metadata?.sources ? (
                                                        <AIAnswerWithCitations
                                                            answer={msg.content}
                                                            sources={msg.metadata.sources}
                                                            onViewFile={(source) => {
                                                                if (source.file_id) {
                                                                    window.open(`/employee/files?preview=${source.file_id}`, '_blank');
                                                                }
                                                            }}
                                                        />
                                                    ) : msg.role === 'assistant' ? (
                                                        <ReactMarkdown
                                                            remarkPlugins={[remarkGfm]}
                                                            components={chatMarkdownComponents}
                                                        >
                                                            {msg.content}
                                                        </ReactMarkdown>
                                                    ) : (
                                                        <span className="whitespace-pre-wrap">{msg.content}</span>
                                                    )}
                                                </div>

                                                {/* File Attachments Display */}
                                                {msg.attachments && msg.attachments.length > 0 && (
                                                    <div className="mt-3 space-y-2">
                                                        {msg.attachments.map((file, idx) => (
                                                            <div
                                                                key={idx}
                                                                className={`flex items-center gap-2 p-2 rounded-lg ${msg.role === 'employee'
                                                                    ? 'bg-white/20'
                                                                    : 'bg-muted border border-border'
                                                                    }`}
                                                            >
                                                                <File size={16} className={msg.role === 'employee' ? 'text-white' : 'text-primary'} />
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-xs font-medium truncate">
                                                                        {file.name}
                                                                    </p>
                                                                    {file.size && (
                                                                        <p className="text-xs opacity-60">
                                                                            {(file.size / 1024).toFixed(1)} KB
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Source Citations for AI messages */}
                                                {msg.role === 'assistant' && msg.metadata?.citations && (
                                                    <SourceCitation citations={msg.metadata.citations} />
                                                )}

                                                {/* Smart Handoff Card - AI suggested a specific person */}
                                                {msg.role === 'assistant' && msg.metadata?.handoff?.primary && (
                                                    <SmartHandoffCard
                                                        handoff={msg.metadata.handoff}
                                                        conversationId={activeConversation?.id}
                                                        onSend={(data) => {
                                                            // Optionally handle post-send (e.g., navigate to messages)
                                                        }}
                                                        onDismiss={() => {
                                                            // Dismissal tracked by the component itself
                                                        }}
                                                    />
                                                )}

                                                {/* Fallback: Generic Message Admin Button (no handoff data) */}
                                                {msg.role === 'assistant' && !msg.metadata?.handoff?.primary && (
                                                    msg.metadata?.suggestAdmin ||
                                                    msg.metadata?.securityOverride ||
                                                    msg.content?.includes('contact your admin') ||
                                                    msg.content?.includes('reach out to HR')
                                                ) && (
                                                        <button
                                                            onClick={() => {
                                                                setAdminMessageContext(msg.content?.substring(0, 200) || '');
                                                                setShowAdminMessageModal(true);
                                                            }}
                                                            className="mt-3 flex items-center gap-2 px-3 py-2 bg-primary/5 hover:bg-primary/10 rounded-lg text-primary text-sm transition-all hover:shadow-md w-full justify-center border border-primary/10"
                                                        >
                                                            <Mail size={16} />
                                                            <span>Message Admin</span>
                                                            <ArrowRight size={14} />
                                                        </button>
                                                    )}

                                                {/* Open in Editor Button - For AI-generated documents */}
                                                {msg.role === 'assistant' && (
                                                    msg.metadata?.document ||
                                                    msg.content?.includes('[Generated Document') ||
                                                    (msg.content?.includes('# ') && msg.content?.includes('## ') && msg.content?.split('\n').length > 15)
                                                ) && !activeDocument && (
                                                        <button
                                                            onClick={() => {
                                                                const content = msg.metadata?.document?.content || msg.content;
                                                                const titleMatch = content?.match(/^#\s+(.+)$/m);
                                                                const documentTitle = msg.metadata?.document?.title || titleMatch?.[1] || 'Generated Document';
                                                                const cleanedContent = content?.replace(/\[Generated Document.*?\]$/s, '').trim();

                                                                setActiveDocument({
                                                                    title: documentTitle,
                                                                    content: cleanedContent,
                                                                    createdAt: new Date().toISOString()
                                                                });
                                                                setEditorContent(cleanedContent || '');
                                                                setHasUnsavedChanges(false);
                                                                toast.success('Document opened in editor!');
                                                            }}
                                                            className="mt-3 flex items-center gap-2 px-3 py-2 bg-green-50 hover:bg-green-100 rounded-lg text-green-600 text-sm transition-all hover:shadow-md w-full justify-center border border-green-100"
                                                        >
                                                            <Edit3 size={16} />
                                                            <span>Open in Editor</span>
                                                            <ArrowRight size={14} />
                                                        </button>
                                                    )}

                                                <div className={`text-xs mt-1.5 ${msg.role === 'employee' ? 'text-white/70' : 'text-muted-foreground'}`}>
                                                    {formatTime(msg.created_at)}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                                {isSending && (
                                    <div className="flex gap-3">
                                        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center animate-pulse shadow-sm">
                                            <Bot size={16} className="text-white" />
                                        </div>
                                        <div className="px-4 py-3 rounded-2xl bg-card border border-border rounded-bl-md shadow-sm">
                                            <div className="flex items-center gap-2">
                                                <div className="flex gap-1.5">
                                                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                                                </div>
                                                <span className="text-xs text-muted-foreground ml-2">AI is thinking...</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* File Processing Progress */}
                            {isProcessingFiles && (
                                <div className="px-4">
                                    <FileProcessingProgress
                                        files={attachedFiles.length > 0 ? attachedFiles : [{ name: 'Processing...' }]}
                                        currentStep={fileProcessingStep}
                                        progress={fileProcessingProgress}
                                    />
                                </div>
                            )}

                            {/* Attached Files Preview */}
                            {attachedFiles.length > 0 && !isProcessingFiles && (
                                <div className="px-4 pt-2">
                                    <div className="flex flex-wrap gap-2 p-3 bg-card rounded-xl border border-border shadow-sm">
                                        {attachedFiles.map((file, idx) => (
                                            <div
                                                key={idx}
                                                className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg text-sm group border border-border"
                                            >
                                                <span>{getFileIcon(file.name)}</span>
                                                <span className="text-foreground truncate max-w-[120px]">{file.name}</span>
                                                <span className="text-xs text-muted-foreground">
                                                    ({(file.size / 1024).toFixed(0)} KB)
                                                </span>
                                                <button
                                                    onClick={() => removeAttachedFile(idx)}
                                                    className="p-0.5 hover:bg-red-50 rounded text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Input - Enhanced glassmorphism style */}
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    attachedFiles.length > 0 ? sendMessageWithFiles() : sendMessage();
                                }}
                                className="flex flex-col gap-2 p-4 bg-card/80 backdrop-blur-md border-t border-border sticky bottom-0"
                            >


                                <div className="flex items-end gap-3">
                                    {/* Attachment Button */}
                                    <button
                                        type="button"
                                        onClick={() => setShowFileAttachmentModal(true)}
                                        disabled={isSending || usage.remaining <= 0}
                                        className="w-11 h-11 flex items-center justify-center rounded-xl bg-secondary hover:bg-muted border border-border text-muted-foreground hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all flex-shrink-0"
                                        title="Attach files"
                                    >
                                        <Paperclip size={20} />
                                    </button>

                                    <textarea
                                        ref={textareaRef}
                                        value={inputMessage}
                                        onChange={(e) => setInputMessage(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Type your message... (Ctrl+Enter to send)"
                                        rows={1}
                                        disabled={isSending || usage.remaining <= 0}
                                        className="flex-1 bg-card border border-border rounded-xl px-4 py-3 text-foreground text-sm resize-none focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed max-h-[150px] transition-all placeholder:text-muted-foreground"
                                    />
                                    <button
                                        type="submit"
                                        disabled={(!inputMessage.trim() && attachedFiles.length === 0) || isSending || usage.remaining <= 0}
                                        className="w-11 h-11 bg-primary hover:bg-primary-hover rounded-xl flex items-center justify-center text-white hover:shadow-lg hover:shadow-primary/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transition-all flex-shrink-0 hover:scale-105"
                                    >
                                        {isSending ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>

                {/* Resize Handle - Enhanced visibility */}
                {activeDocument && !isEditorFullscreen && (
                    <div
                        onMouseDown={() => setIsDragging(true)}
                        className="w-4 hover:w-5 -ml-2 z-20 bg-transparent hover:bg-primary/10 cursor-col-resize flex items-center justify-center transition-all group relative"
                    >
                        <div className="absolute inset-y-0 w-px bg-border group-hover:bg-primary/50 transition-colors" />
                        <div className="w-6 h-12 bg-card border border-border rounded-full flex items-center justify-center shadow-sm group-hover:border-primary/30 group-hover:shadow-md transition-all">
                            <GripVertical size={14} className="text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                    </div>
                )}

                {/* Document Editor */}
                {activeDocument && (
                    <div
                        className="overflow-hidden transition-all duration-300"
                        style={{ width: `${editorWidth}%` }}
                    >
                        <DocumentEditor
                            document={activeDocument}
                            onClose={handleCloseEditor}
                            onSave={handleSaveToFiles}
                            onDownload={handleDownload}
                            isFullscreen={isEditorFullscreen}
                            onToggleFullscreen={() => setIsEditorFullscreen(!isEditorFullscreen)}
                            updateStatus={updateStatus}
                            hasUnsavedChanges={hasUnsavedChanges}
                            setHasUnsavedChanges={setHasUnsavedChanges}
                            editorContent={editorContent}
                            setEditorContent={setEditorContent}
                        />
                    </div>
                )}
            </main>

            {/* Modals */}
            <FormatModal
                isOpen={showFormatModal}
                onClose={() => setShowFormatModal(false)}
                onSelect={handleFormatSelect}
            />

            <SaveAsModal
                isOpen={showSaveAsModal}
                onClose={() => setShowSaveAsModal(false)}
                onSave={handleSaveWithName}
                defaultTitle={activeDocument?.title || 'AI Generated Document'}
                isSaving={isSavingDocument}
            />

            <SaveConfirmModal
                isOpen={showSaveConfirmModal}
                onClose={() => setShowSaveConfirmModal(false)}
                onViewFiles={handleViewInFiles}
                onDownloadAlso={handleDownloadAlso}
            />

            <UnsavedWarningModal
                isOpen={showUnsavedWarning}
                onClose={() => setShowUnsavedWarning(false)}
                onDiscard={closeEditor}
                onSave={handleSaveToFiles}
                onDownload={handleDownload}
            />

            <UnifiedFilePickerModal
                isOpen={showFileAttachmentModal}
                onClose={() => setShowFileAttachmentModal(false)}
                onAttach={handleFilesSelected}
                allowMultiple={true}
                title="Attach Files"
            />

            <AdminMessageModal
                isOpen={showAdminMessageModal}
                onClose={() => setShowAdminMessageModal(false)}
                onSent={(data) => console.log('Message sent:', data)}
                context={adminMessageContext}
                navigate={navigate}
            />

            <PlatformGuideModal
                isOpen={showPlatformGuide}
                onClose={() => setShowPlatformGuide(false)}
            />
        </div>
    );
};

export default EmployeeAIPage;
